import { useCallback, useEffect, useRef, useState } from 'react'
import { postDemoPrompt, type SessionUser } from '../auth/api'
import type { AccountId } from '../data/account'
import type { Client } from '../data/clients'
import { deleteCompany } from '../data/members'
import { can } from '../lib/roles'
import {
  connectAccount,
  fetchAvailableAccounts,
  removeAccount,
  type AvailableAccount,
  type MutationResult,
} from '../data/connections'

/**
 * What the panel says after a mutation that did not simply succeed. `refreshFailed` is the
 * one the reader can act on: the companies could not be refetched afterwards, so the list
 * may be out of date, and `retry` reconciles again.
 */
export type ConnectionNotice = 'notAvailable' | 'alreadyRemoved' | 'unreachable' | 'refreshFailed' | null

export interface AccountConnections {
  /** Exposed accounts the active company may connect; empty until the first load. */
  available: AvailableAccount[]
  /** The last load of that list failed: what is shown may be out of date or missing. */
  availableFailed: boolean
  /** Loads the list again. */
  reloadAvailable: () => void
  /** The exposed or account id a mutation is in flight for. Its button is disabled. */
  pending: string | null
  notice: ConnectionNotice
  /** An Owner or Admin removes a legacy demo account from a real company (GRO-131, D4). */
  canRemoveDemo: boolean
  /**
   * A connect just succeeded and the session says the question about the user's own demo
   * company is still to ask (GRO-131, D5).
   */
  demoPrompt: boolean
  /** Delete removes the demo company; Keep records the answer. Either ends the question. */
  answerDemoPrompt: (answer: 'delete' | 'keep') => void
  connect: (exposedId: string) => void
  removeDemo: (accountId: AccountId) => void
  /** Reconciles again after `refreshFailed`. */
  retry: () => void
}

function sameIds(a: Client | undefined, b: Client | undefined): boolean {
  const ids = (client: Client | undefined) => (client?.accounts ?? []).map((account) => account.id).sort().join(',')
  return ids(a) === ids(b)
}

function noticeOf(result: MutationResult): ConnectionNotice {
  if (result.status === 'ok') return null
  if (result.status === 'unreachable') return 'unreachable'
  if (result.code === 'not_found') return 'alreadyRemoved'
  return 'notAvailable'
}

/**
 * The account panel's connection surface for the active company.
 *
 * **Every mutation attempt ends in a reconcile, whatever its outcome** (GRO-41 design,
 * section 7): success, a refusal and a lost reply all refetch the companies and the
 * available list, and `onAccountsChanged` fires whenever the refetched set of account ids
 * differs from the one before the attempt. A connect or removal that committed but whose
 * reply never arrived therefore still lands the panel, and through the shell's scope key
 * the reporting on screen, in the committed state.
 */
export function useAccountConnections(
  client: Client,
  refreshClients: () => Promise<Client[] | null>,
  onAccountsChanged: (clientId: string) => void,
  /** Whether the account panel is open. Every opening reloads the list. */
  panelOpen: boolean,
  fetchImpl: typeof fetch = fetch,
  /** Off for a guest (GRO-83): no account list is read. */
  enabled = true,
  /** The session user, for the demo company question after a connect (GRO-131, D5). */
  user?: SessionUser,
): AccountConnections {
  const [available, setAvailable] = useState<{ clientId: string; accounts: AvailableAccount[]; failed: boolean }>(
    // No company yet, so the first render counts as a company change and loads the list.
    () => ({ clientId: '', accounts: [], failed: false }),
  )
  const [pending, setPending] = useState<string | null>(null)
  // A connect succeeded; whether to ask is read from the session the connect reconciled.
  const [connected, setConnected] = useState(false)
  const [notice, setNotice] = useState<{ clientId: string; notice: ConnectionNotice }>(
    () => ({ clientId: client.id, notice: null }),
  )
  const active = useRef(client.id)
  active.current = client.id
  // Every load of a company's list takes that company's next generation, and a mutation
  // takes one before it starts. A response lands only while its generation is still the
  // latest, so a read that was in flight when a connect began can never write the
  // pre-connect list back over the list the connect's reconcile loaded. The count is per
  // company: a reconcile finishing for a company the operator has left must not
  // invalidate the load already running for the one now open.
  const generations = useRef(new Map<string, number>())
  const nextGeneration = useCallback((clientId: string): number => {
    const next = (generations.current.get(clientId) ?? 0) + 1
    generations.current.set(clientId, next)
    return next
  }, [])

  /**
   * Loads the list and says whether it could. A failure keeps what is listed and marks it,
   * so the panel says the list may be out of date and offers a retry, and the next opening
   * of the panel loads it again anyway.
   */
  const loadAvailable = useCallback(async (clientId: string): Promise<boolean> => {
    const mine = nextGeneration(clientId)
    const result = await fetchAvailableAccounts(clientId, fetchImpl)
    if (mine !== generations.current.get(clientId) || active.current !== clientId) return result.status === 'ok'
    if (result.status === 'ok') {
      setAvailable({ clientId, accounts: result.accounts, failed: false })
      return true
    }
    setAvailable((prev) => (prev.clientId === clientId
      ? { ...prev, failed: true }
      : { clientId, accounts: [], failed: true }))
    return false
  }, [fetchImpl, nextGeneration])

  useEffect(() => {
    // On a company change, and on every opening of the panel: the operator may have added
    // a claim since the list was last read.
    if (!enabled) return
    if (panelOpen || available.clientId !== client.id) void loadAvailable(client.id)
    // `available.clientId` is read to tell a company change apart, not to react to loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id, panelOpen, loadAvailable, enabled])

  const reloadAvailable = useCallback(() => {
    void loadAvailable(client.id)
  }, [client.id, loadAvailable])

  /**
   * Refetches the companies and the available list, and reports whether the account set
   * changed since `before`. A refetch that fails cannot say, and reads as changed: the
   * reporting is invalidated anyway, because a mutation may have committed, and showing
   * figures from an account that is gone is worse than one refetch too many.
   */
  const reconcile = useCallback(async (clientId: string, before: Client): Promise<boolean> => {
    const clients = await refreshClients()
    const after = clients?.find((c) => c.id === clientId)
    if (after === undefined || !sameIds(before, after)) onAccountsChanged(clientId)
    const listed = await loadAvailable(clientId)
    return after !== undefined && listed
  }, [refreshClients, onAccountsChanged, loadAvailable])

  // The account set a failed reconcile compares against when it is retried.
  const unreconciled = useRef<{ clientId: string; before: Client } | null>(null)

  const mutate = useCallback(async (id: string, run: () => Promise<MutationResult>, isConnect = false) => {
    const clientId = client.id
    const before = client
    setPending(id)
    setNotice({ clientId, notice: null })
    nextGeneration(clientId)
    const result = await run()
    const refreshed = await reconcile(clientId, before)
    unreconciled.current = refreshed ? null : { clientId, before }
    if (isConnect && result.status === 'ok') setConnected(true)
    if (active.current === clientId) {
      setNotice({ clientId, notice: refreshed ? noticeOf(result) : 'refreshFailed' })
    }
    setPending(null)
  }, [client, reconcile, nextGeneration])

  const retry = useCallback(() => {
    const pendingRetry = unreconciled.current
    if (pendingRetry === null || pendingRetry.clientId !== client.id) return
    setPending('retry')
    void reconcile(pendingRetry.clientId, pendingRetry.before).then((refreshed) => {
      if (refreshed) unreconciled.current = null
      if (active.current === pendingRetry.clientId) {
        setNotice({ clientId: pendingRetry.clientId, notice: refreshed ? null : 'refreshFailed' })
      }
      setPending(null)
    })
  }, [client.id, reconcile])

  const connect = useCallback((exposedId: string) => {
    void mutate(exposedId, () => connectAccount(client.id, exposedId, fetchImpl), true)
  }, [mutate, client.id, fetchImpl])

  const demoCompanyId = user?.demoCompanyId ?? null
  const answerDemoPrompt = useCallback((answer: 'delete' | 'keep') => {
    setConnected(false)
    void (async () => {
      if (answer === 'delete' && demoCompanyId !== null) {
        const outcome = await deleteCompany(demoCompanyId)
        // Membership lost or the company already gone: nothing is left to delete, so the
        // question ends as if kept.
        const gone = outcome.status === 'forbidden' || outcome.status === 'notFound'
          || (outcome.status === 'refused' && outcome.code === 'company_deleted')
        if (gone) await postDemoPrompt('keep', fetchImpl)
      } else {
        await postDemoPrompt('keep', fetchImpl)
      }
      await refreshClients()
    })()
  }, [demoCompanyId, fetchImpl, refreshClients])


  const removeDemo = useCallback((accountId: AccountId) => {
    void mutate(accountId, () => removeAccount(client.id, accountId, fetchImpl))
  }, [mutate, client.id, fetchImpl])

  return {
    available: available.clientId === client.id ? available.accounts : [],
    availableFailed: available.clientId === client.id && available.failed,
    reloadAvailable,
    pending,
    notice: notice.clientId === client.id ? notice.notice : null,
    canRemoveDemo: can(client.role, 'connectAccount') && !client.isDemo,
    demoPrompt: connected && user?.demoPromptPending === true && demoCompanyId !== null && demoCompanyId !== client.id,
    answerDemoPrompt,
    connect,
    removeDemo,
    retry,
  }
}
